import * as React from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusPill,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  cn,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  History,
  Loader2,
  Mail,
  MoreHorizontal,
  Phone,
  Save,
  Trash2,
  UserRound,
  XOctagon,
} from "lucide-react";
import type { AdmissionFunnelStage } from "@eduviaos/shared";
import {
  useActiveForm,
  useAdvanceEnquiry,
  useEnquiry,
  useFunnel,
  useSaveStagePayload,
  useStaffRoster,
} from "@/hooks/useAdmissions";
import { isValidEnquiryId } from "@/lib/admissions/enquiryIdGuard";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { getVerticalProfile } from "@/lib/verticalProfile";
import { StageFormRenderer } from "@/components/StageFormRenderer";
import { EnquiryRejectDialog } from "@/components/EnquiryRejectDialog";
import { EnquiryDeleteDialog } from "@/components/EnquiryDeleteDialog";
import { EnquiryConvertDialog } from "@/components/EnquiryConvertDialog";
import { AssignmentMenu } from "@/components/AssignmentMenu";
import { CollaboratorsCard } from "@/components/CollaboratorsCard";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { AddActivityDialog } from "@/components/AddActivityDialog";
import { FollowupDialog } from "@/components/FollowupDialog";
import { AISuggestionChip } from "@/components/AISuggestionChip";
import { StageProgressPanel } from "@/components/StageProgressPanel";
import { ProctorReviewPanel } from "@/components/proctor/ProctorReviewPanel";
import { OnlineInterviewSection } from "@/components/admissions/OnlineInterviewSection";

/**
 * Enquiry Detail page — Phase D1b
 * --------------------------------------------------------------
 * The IA's daily workhorse. Replaces the right-side drawer pattern
 * with a route so deep links work (Slack, Linear, email handoffs)
 * and so the address bar reflects the IA's mental focus.
 *
 * Layout (1280px+):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Header: name · stage pill · breadcrumbs · actions menu  │
 *   │ KPI strip: created · last advanced · stages walked      │
 *   ├────────────────────────────────────┬─────────────────────┤
 *   │ Active stage form (2/3)            │ Contact card (1/3)  │
 *   │ Stage history timeline             │ Source / assignee  │
 *   │                                    │ Audit meta         │
 *   ├────────────────────────────────────┴─────────────────────┤
 *   │ Action bar: Save · Save & Advance · Reject · Convert    │
 *   └──────────────────────────────────────────────────────────┘
 *
 * <768px collapses to a single column with the contact card pinned
 * to the top so a counsellor on a phone always sees who they're
 * about to call.
 *
 * State-flow rules:
 *   - The form panel is locally-controlled; we hydrate from
 *     `active_form.saved_payload` whenever the active form changes
 *     (which fires on stage advance too).
 *   - Save = `PUT /stage-payload` (no stage change).
 *   - Save & Advance = `PUT /stage-payload/{currentStage}` (persists the
 *     locally-edited values for the stage we're leaving) followed by
 *     `POST /advance` WITHOUT a payload. The BE's `/advance` accepts
 *     `payload` ONLY for the destination stage, so sending current-stage
 *     values there triggers `STAGE_HAS_NO_FORM` whenever the destination
 *     isn't form-linked (the common mid-funnel case — see F-017).
 *   - Reject / Convert / Delete are dialog-confirmed from the
 *     header dropdown.
 */

interface BeFieldError {
  field_key: string;
  code: string;
  message: string;
}

interface BeError {
  code?: string;
  message?: string;
  details?: { field_errors?: BeFieldError[] };
}

function readBeError(err: unknown): BeError | null {
  if (!err || typeof err !== "object") return null;
  const e = err as BeError;
  if (!e.code) return null;
  return { code: e.code, message: e.message ?? "", details: e.details };
}

function pillVariant(stage?: AdmissionFunnelStage): StatusPillStatus {
  if (!stage) return "invited";
  if (stage.terminal_outcome === "admitted") return "active";
  if (stage.terminal_outcome === "rejected") return "archived";
  return "invited";
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function EnquiryDetailPage() {
  const { enquiryId = "" } = useParams<{ enquiryId: string }>();
  const navigate = useNavigate();
  const { vertical: tenantVertical } = useEnabledModules();
  const hasParents = getVerticalProfile(tenantVertical ?? "school").has.parents;

  // Phase H9 — Admissions invalid-URL guard. Sibling sub-paths like
  // `/admissions/configure` are caught by the `:enquiryId` route and,
  // pre-H9, fired straight at the BE, which rejected them with
  // [net::ERR_FAILED] / 4xx. The page then rendered a misleading
  // "Try refreshing" card. Now we short-circuit at the client: if the
  // param isn't a 24-hex ObjectId we pass `null` to the data hooks
  // (skipping the doomed network call) and render a tailored
  // "Invalid enquiry URL" state below.
  const isValidId = isValidEnquiryId(enquiryId);
  const safeEnquiryIdForFetch = isValidId ? enquiryId : null;

  const { data: enquiry, isLoading, isError, error } = useEnquiry(safeEnquiryIdForFetch);
  const { data: activeForm, isLoading: formLoading } = useActiveForm(safeEnquiryIdForFetch);
  const { data: funnel } = useFunnel();

  const saveMutation = useSaveStagePayload(enquiryId);
  const advanceMutation = useAdvanceEnquiry(enquiryId);

  const [values, setValues] = React.useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  // Validation-summary banner — kept inline (NOT migrated to toast)
  // because it summarises the count of per-field errors rendered
  // *below* the banner on the form. Toasting the summary would
  // separate it from the inline field markers, forcing the IA to
  // mentally reconnect "3 fields need attention" with the highlighted
  // rows. Decision rule reaffirmed in §E1.b.4.1 Catalogue + Holidays.
  const [validationSummary, setValidationSummary] = React.useState<string | null>(null);
  const [openDialog, setOpenDialog] = React.useState<
    null | "reject" | "delete" | "convert" | "followup" | "activity"
  >(null);

  // Hydrate the editor whenever the active form swaps (e.g. on stage
  // advance the BE returns the next stage's saved payload). The deps
  // intentionally include `slug` so a same-stage form-version bump
  // also rehydrates.
  React.useEffect(() => {
    if (!enquiryId) return;
    setValues((activeForm?.saved_payload as Record<string, unknown>) ?? {});
    setFieldErrors({});
    setValidationSummary(null);
  }, [enquiryId, activeForm?.saved_payload, activeForm?.form?.slug]);

  // Funnel-driven next-stage resolution. The funnel orders stages by
  // `order` ascending; the next non-terminal slot is "advance". If
  // the only candidate is a terminal slot we still surface it (the
  // BE will accept advance into terminal stages from the dropdown
  // menu actions; explicit reject/convert paths are preferred but
  // we don't outright deny it here).
  //
  // ⚠️ All hooks below MUST run unconditionally on every render so
  // React's invariant holds. The route-level bail-out (empty
  // `enquiryId`) and the data-fetch gates (loading / error / null
  // enquiry) come AFTER the hook calls.
  const stages = React.useMemo(
    () => (funnel ? [...funnel.stages].sort((a, b) => a.order - b.order) : []),
    [funnel],
  );
  const currentStageObj = React.useMemo(() => {
    if (!enquiry || !stages.length) return undefined;
    return stages.find((s) => s.stage_key === enquiry.stage_key);
  }, [enquiry, stages]);

  const nextStageObj = React.useMemo(() => {
    if (!enquiry || !stages.length) return undefined;
    const i = stages.findIndex((s) => s.stage_key === enquiry.stage_key);
    if (i < 0) return undefined;
    // Look forward for the next non-terminal stage; that's the
    // "Save & Advance" target. If everything ahead is terminal,
    // surface nothing — the IA must use Convert/Reject instead.
    for (let j = i + 1; j < stages.length; j += 1) {
      const s = stages[j];
      if (s && !s.is_terminal) return s;
    }
    return undefined;
  }, [enquiry, stages]);

  const handleErr = React.useCallback((err: unknown) => {
    const be = readBeError(err);
    if (be?.code === "PAYLOAD_INVALID") {
      const fe = be.details?.field_errors ?? [];
      const map: Record<string, string> = {};
      for (const e of fe) map[e.field_key] = e.message;
      setFieldErrors(map);
      // Inline validation summary anchors the per-field markers.
      setValidationSummary(
        `${fe.length} field${fe.length === 1 ? "" : "s"} need attention before saving.`,
      );
      return;
    }
    if (be?.code === "STAGE_HAS_NO_FORM") {
      toast.error(
        "This stage has no form configured. Ask a Super Admin to link one in the Admissions tab.",
      );
      return;
    }
    toast.error(be?.message ?? "Something went wrong. Please try again.");
  }, []);

  const onSave = async () => {
    if (!activeForm?.form) return;
    setFieldErrors({});
    setValidationSummary(null);
    try {
      await saveMutation.mutateAsync({
        stageKey: activeForm.stage_key,
        payload: values,
      });
      toast.success("Saved.");
    } catch (e) {
      handleErr(e);
    }
  };

  const onSaveAndAdvance = async () => {
    if (!nextStageObj) return;
    setFieldErrors({});
    setValidationSummary(null);
    try {
      // F-017 fix: the BE `/advance` endpoint treats `payload` as
      // belonging to the DESTINATION stage; sending the operator's
      // current-stage form values there triggers a 422
      // `STAGE_HAS_NO_FORM` whenever the destination has no linked
      // form (the common mid-funnel case). Persist the current-stage
      // payload first via the proven `PUT /stage-payload/{currentStage}`
      // path, then advance with no payload. If save throws (e.g.
      // PAYLOAD_INVALID), `handleErr` surfaces inline field markers
      // and the advance is intentionally aborted so the operator
      // never lands on the next stage with bad data behind them.
      if (activeForm?.form) {
        await saveMutation.mutateAsync({
          stageKey: activeForm.stage_key,
          payload: values,
        });
      }
      await advanceMutation.mutateAsync({
        to_stage_key: nextStageObj.stage_key,
      });
      toast.success(`Advanced to ${nextStageObj.label}.`);
    } catch (e) {
      handleErr(e);
    }
  };

  // ── Bail-out / loading / error gates (post-hooks) ──────────────────────
  if (!enquiryId) return <Navigate to="/admissions" replace />;
  // Phase H9 — Invalid URL takes priority over the "couldn't load /
  // network error" branch because the BE call was skipped entirely
  // for malformed IDs. Copy names the offending value so a Slack
  // recipient who clicked a bad link knows the URL was the problem.
  if (!isValidId) {
    return (
      <NotFoundOrError
        title="Invalid enquiry URL"
        description={`"${enquiryId}" isn't a valid enquiry. Open one from the enquiry list to continue.`}
        testId="enquiry-invalid-url"
      />
    );
  }
  if (isLoading) return <DetailSkeleton />;
  if (isError || !enquiry) {
    const code = (error as BeError | null)?.code;
    return (
      <NotFoundOrError
        title={code === "NOT_FOUND" ? "Enquiry not found" : "Couldn't load enquiry"}
        description={
          code === "NOT_FOUND"
            ? "This enquiry may have been deleted, or you don't have access to it."
            : "Try refreshing — if it keeps happening, check your network."
        }
      />
    );
  }

  // Soft-delete is always filtered server-side on the default GET, so
  // the detail route 404s deleted enquiries today. A "deleted items"
  // view + restore affordance lands in a follow-up slice.
  const isBusy = saveMutation.isPending || advanceMutation.isPending;
  const isRejected = currentStageObj?.terminal_outcome === "rejected";
  const isAdmitted = currentStageObj?.terminal_outcome === "admitted";

  // Public-intake metadata. The BE flags soft duplicates (same parent
  // phone within 24h) so the IA can spot a sibling enquiry / a
  // double-submit before they spend time on a fresh outreach. We
  // surface a chip-style banner with a deep-link to the original.
  const submittedViaPublic =
    (enquiry.metadata?.submitted_via as string | undefined) === "public_intake";
  const possibleDuplicate = enquiry.metadata?.possible_duplicate === true;
  const duplicateOfId = enquiry.metadata?.duplicate_of_enquiry_id as string | null | undefined;
  // Whether the enquiry has been promoted to an Admission record
  // already. The BE writes this back-pointer atomically on convert, so
  // we use it (not the funnel stage) to decide whether to show the
  // Convert action — an enquiry can sit at terminal-admitted stage for
  // a while before someone actually clicks Convert, and during that
  // window we still want the action available.
  const isConverted = enquiry.converted_to_admission_id != null;

  return (
    <>
      <Breadcrumb className="mb-3">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admissions">Admissions</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="max-w-[60ch] truncate">
              {enquiry.student_name}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading className="flex items-center gap-3">
              <span className="truncate">{enquiry.student_name}</span>
              <StatusPill status={pillVariant(currentStageObj)}>
                {currentStageObj?.label ?? enquiry.stage_key}
              </StatusPill>
            </PageHeaderHeading>
            <PageHeaderDescription>
              Created {formatDateTime(enquiry.created_at)}
              {enquiry.last_advanced_at && (
                <>
                  <span className="text-muted-foreground/60 mx-2">·</span>
                  Last advanced {formatDateTime(enquiry.last_advanced_at)}
                </>
              )}
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button variant="ghost" size="sm" onClick={() => navigate("/admissions")}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              Back to list
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="enquiry-detail-actions">
                  <MoreHorizontal className="size-4" aria-hidden="true" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                {!isRejected && !isConverted && (
                  <DropdownMenuItem
                    onSelect={() => setOpenDialog("convert")}
                    className="text-emerald-700 focus:text-emerald-700 dark:text-emerald-400 dark:focus:text-emerald-400"
                  >
                    <GraduationCap className="size-4" aria-hidden="true" />
                    Convert to admission…
                  </DropdownMenuItem>
                )}
                {!isRejected && !isConverted && (
                  <DropdownMenuItem
                    onSelect={() => setOpenDialog("reject")}
                    className="text-destructive focus:text-destructive"
                  >
                    <XOctagon className="size-4" aria-hidden="true" />
                    Reject…
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => setOpenDialog("delete")}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                  Move to bin…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {/* ── Public-intake duplicate banner ─────────────────────────────────── */}
      {submittedViaPublic && possibleDuplicate && (
        <div
          role="status"
          data-testid="duplicate-banner"
          className={cn(
            "mt-4 flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-xs",
            "border-amber-300/60 bg-amber-50 text-amber-900",
            "dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200",
          )}
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <div className="flex-1 leading-relaxed">
            <p className="font-medium">Possible duplicate submission</p>
            <p className="text-amber-800/90 dark:text-amber-300/90">
              {hasParents
                ? "The same parent phone was used to submit another enquiry in the last 24 hours. This is often a sibling — but it can also be a fast-fingered double submit. Please check the original before reaching out."
                : "The same phone number was used to submit another enquiry in the last 24 hours. It may be a double submit. Please check the original before reaching out."}
            </p>
            {duplicateOfId && (
              <Link
                to={`/admissions/${duplicateOfId}`}
                className="mt-1 inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
              >
                View the earlier enquiry
                <ArrowRight className="size-3" aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Validation-summary banner (decision rule: anchors per-field
          markers; toasting would separate the summary from the
          highlighted rows). Non-validation errors and successes ride
          the global toast layer. */}
      {validationSummary && (
        <div className="mt-4">
          <div
            role="alert"
            className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          >
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{validationSummary}</span>
          </div>
        </div>
      )}

      {/* ── Body grid ──────────────────────────────────────────────────────── */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {/* Active stage form */}
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader className="border-border/60 border-b pb-4">
              <CardTitle className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2">
                  <ClipboardList className="text-muted-foreground size-4" aria-hidden="true" />
                  {activeForm?.form ? activeForm.form.name : "Stage form"}
                </span>
                {activeForm?.form && (
                  <span className="text-muted-foreground text-[10px] uppercase tracking-wider">
                    v{activeForm.form.version}
                  </span>
                )}
              </CardTitle>
              {activeForm?.saved_payload_meta && (
                <p className="text-muted-foreground mt-1 text-xs">
                  Last saved {formatDateTime(activeForm.saved_payload_meta.saved_at)}
                </p>
              )}
            </CardHeader>
            <CardContent className="pt-5">
              {formLoading ? (
                <FormSkeleton />
              ) : !activeForm?.form ? (
                <NoFormEmptyState stageKey={enquiry.stage_key} />
              ) : (
                <StageFormRenderer
                  fields={activeForm.form.fields}
                  values={values}
                  onChange={setValues}
                  fieldErrors={fieldErrors}
                  disabled={isBusy || isRejected || isAdmitted}
                />
              )}
            </CardContent>
          </Card>

          <ActivityTimeline enquiry={enquiry} onAddActivity={() => setOpenDialog("activity")} />

          {/* Phase F11 FE-3 — proctor review. Renders a card with
              one tab per proctored attempt; gracefully renders
              nothing when the enquiry has no attempts at all, and a
              friendly explainer when none of them ran with
              proctoring. The panel is keyed on the enquiry id so it
              automatically refetches when the user navigates between
              two enquiries via deep links. */}
          <ProctorReviewPanel key={enquiry.id} enquiryId={enquiry.id} />

          <StageTimeline
            history={enquiry.stage_history}
            stages={stages}
            currentStageKey={enquiry.stage_key}
          />
        </div>

        {/* Right rail */}
        <div className="space-y-6 lg:col-span-1">
          {/* Phase F0 — action-gated stage engine. Sits ABOVE the AI
              chip because "what's stopping me from advancing this?"
              is the very first question a counsellor opens this page
              with; the AI nudge comes after they've understood the
              concrete state. */}
          {!isRejected && !isAdmitted && <StageProgressPanel enquiryId={enquiry.id} />}
          {/* Phase F13 FE-1 — online interview management. Surfaces
              right under the stage panel because the candidate-facing
              "schedule the next interview" action is the most common
              follow-up to "this stage isn't complete yet". The
              section gracefully self-hides its CTA on non-interview
              stages (the BE 409s anyway). */}
          {!isRejected && !isAdmitted && (
            <OnlineInterviewSection enquiryId={enquiry.id} currentStageKey={enquiry.stage_key} />
          )}
          {/* Phase E2b — AI counsellor suggestion. Sits below the
              stage panel so the IA reads concrete state first, then
              decides whether they want a nudge. */}
          <AISuggestionChip enquiryId={enquiry.id} />
          <ContactCard enquiry={enquiry} />
          <OwnershipCard enquiry={enquiry} onEditFollowup={() => setOpenDialog("followup")} />
          <CollaboratorsCard enquiry={enquiry} />
          <MetaCard enquiry={enquiry} />
        </div>
      </div>

      {/* ── Sticky action bar ──────────────────────────────────────────────── */}
      {!isRejected && !isAdmitted && (
        <div
          className={cn(
            "border-border/60 bg-background/95 sticky bottom-0 -mx-4 mt-6 flex items-center justify-end gap-2 border-t px-6 py-3 backdrop-blur-sm",
            "supports-[backdrop-filter]:bg-background/80",
          )}
        >
          <Button
            variant="outline"
            onClick={onSave}
            disabled={isBusy || !activeForm?.form}
            data-testid="detail-save"
          >
            {saveMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="size-4" aria-hidden="true" />
            )}
            Save
          </Button>
          {nextStageObj && (
            <Button onClick={onSaveAndAdvance} disabled={isBusy} data-testid="detail-save-advance">
              {advanceMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="size-4" aria-hidden="true" />
              )}
              {activeForm?.form
                ? `Save & advance to ${nextStageObj.label}`
                : `Advance to ${nextStageObj.label}`}
            </Button>
          )}
        </div>
      )}

      {/* ── Confirmation dialogs ───────────────────────────────────────────── */}
      <EnquiryRejectDialog
        open={openDialog === "reject"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        enquiryId={enquiry.id}
        studentName={enquiry.student_name}
      />
      <EnquiryDeleteDialog
        open={openDialog === "delete"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        enquiryId={enquiry.id}
        studentName={enquiry.student_name}
        onDeleted={() => navigate("/admissions")}
      />
      <EnquiryConvertDialog
        open={openDialog === "convert"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        enquiryId={enquiry.id}
        studentName={enquiry.student_name}
      />
      <FollowupDialog
        open={openDialog === "followup"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        enquiry={enquiry}
      />
      <AddActivityDialog
        open={openDialog === "activity"}
        onOpenChange={(o) => !o && setOpenDialog(null)}
        enquiry={enquiry}
      />
    </>
  );
}

// ── Components ─────────────────────────────────────────────────────────────

function ContactCard({ enquiry }: { enquiry: NonNullable<ReturnType<typeof useEnquiry>["data"]> }) {
  // SC Foundation (F0d) — relabel "Student" → the vertical's person noun
  // and drop the parent/guardian block for verticals without parents
  // (study centre). School / coaching are unchanged.
  const { vertical } = useEnabledModules();
  const profile = getVerticalProfile(vertical ?? "school");
  const showParent = profile.has.parents;
  return (
    <Card>
      <CardHeader className="border-border/60 border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <UserRound className="text-muted-foreground size-4" aria-hidden="true" />
          Contact
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-4">
        <ContactRow
          icon={<UserRound aria-hidden="true" />}
          label={profile.personNoun.one}
          value={enquiry.student_name}
        />
        <ContactRow
          icon={<Mail aria-hidden="true" />}
          label="Email"
          value={enquiry.student_email}
          mailto
        />
        <ContactRow
          icon={<Phone aria-hidden="true" />}
          label="Phone"
          value={enquiry.student_phone}
          tel
        />
        {showParent && (enquiry.parent_name || enquiry.parent_phone) && (
          <>
            <div className="border-border/60 border-t pt-3">
              <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
                Parent / Guardian
              </p>
            </div>
            <ContactRow
              icon={<UserRound aria-hidden="true" />}
              label="Name"
              value={enquiry.parent_name}
            />
            <ContactRow
              icon={<Phone aria-hidden="true" />}
              label="Phone"
              value={enquiry.parent_phone}
              tel
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ContactRow({
  icon,
  label,
  value,
  mailto,
  tel,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  mailto?: boolean;
  tel?: boolean;
}) {
  const display = value || "—";
  const href = mailto && value ? `mailto:${value}` : tel && value ? `tel:${value}` : null;
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-muted-foreground mt-0.5 inline-flex size-4 shrink-0 [&_svg]:size-4">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
          {label}
        </div>
        {href && value ? (
          <a
            href={href}
            className="text-foreground hover:text-primary block truncate text-sm transition-colors"
          >
            {display}
          </a>
        ) : (
          <div className="text-foreground truncate text-sm">{display}</div>
        )}
      </div>
    </div>
  );
}

/**
 * OwnershipCard — Phase D5 + D3.
 *
 * Sits at the top of the right rail (just below contact). Combines:
 *   - Primary owner picker (`AssignmentMenu`) — bound to /admissions/staff
 *   - Next follow-up chip — opens `FollowupDialog` for set/clear
 *
 * Why a single card?
 *   "Who owns this and when do they next chase it" is a single decision
 *   for the IA. Splitting them into two cards added scroll without adding
 *   information density. The chip and the menu sit side-by-side at sm+
 *   and stack at xs.
 */
function OwnershipCard({
  enquiry,
  onEditFollowup,
}: {
  enquiry: NonNullable<ReturnType<typeof useEnquiry>["data"]>;
  onEditFollowup: () => void;
}) {
  const followup = enquiry.next_followup_at ? new Date(enquiry.next_followup_at) : null;
  const now = new Date();
  const overdue = followup !== null && followup.getTime() < now.getTime();
  const isToday =
    followup !== null &&
    followup.getFullYear() === now.getFullYear() &&
    followup.getMonth() === now.getMonth() &&
    followup.getDate() === now.getDate();

  const followupLabel = followup
    ? followup.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "Not scheduled";

  const toneClass = overdue
    ? "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300"
    : isToday
      ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
      : followup
        ? "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300"
        : "border-border/60 bg-muted/40 text-muted-foreground";

  return (
    <Card>
      <CardHeader className="border-border/60 border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <UserRound className="text-muted-foreground size-4" aria-hidden="true" />
          Ownership
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        <div>
          <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
            Primary owner
          </p>
          <div className="mt-1.5">
            <AssignmentMenu
              enquiryId={enquiry.id}
              currentUserId={enquiry.assigned_to}
              triggerClassName="w-full justify-between"
            />
          </div>
        </div>

        <div>
          <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
            Next follow-up
          </p>
          <button
            type="button"
            onClick={onEditFollowup}
            className={cn(
              "mt-1.5 flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-xs transition-colors",
              toneClass,
              "focus-visible:outline-hidden focus-visible:ring-primary/40 hover:opacity-90 focus-visible:ring-2",
            )}
            aria-label={
              followup ? `Edit follow-up scheduled for ${followupLabel}` : "Schedule a follow-up"
            }
          >
            <span className="flex min-w-0 items-center gap-2">
              <CalendarClock className="size-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate font-medium">
                {overdue ? "Overdue · " : isToday ? "Today · " : ""}
                {followupLabel}
              </span>
            </span>
            <span className="text-[10px] uppercase tracking-wider opacity-70">
              {followup ? "Edit" : "Set"}
            </span>
          </button>
          {enquiry.next_followup_note && (
            <p className="text-muted-foreground mt-2 line-clamp-2 text-xs leading-relaxed">
              {enquiry.next_followup_note}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MetaCard({ enquiry }: { enquiry: NonNullable<ReturnType<typeof useEnquiry>["data"]> }) {
  return (
    <Card>
      <CardHeader className="border-border/60 border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <ChevronRight className="text-muted-foreground size-4" aria-hidden="true" />
          Tracking
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-4 text-xs">
        <Row label="Source">
          {enquiry.source ? (
            <Badge variant="outline" className="font-normal">
              {enquiry.source.replace(/_/g, " ")}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Row>
        <Row label="Branch">
          <span className="text-foreground tabular-nums">{enquiry.branch_id ?? "—"}</span>
        </Row>
        <Row label="Created">
          <span className="text-muted-foreground tabular-nums">
            {formatDateTime(enquiry.created_at)}
          </span>
        </Row>
        <Row label="Last advanced">
          <span className="text-muted-foreground tabular-nums">
            {formatDateTime(enquiry.last_advanced_at)}
          </span>
        </Row>
        {enquiry.notes && (
          <div className="border-border/60 border-t pt-3">
            <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
              Notes
            </p>
            <p className="text-foreground mt-1 whitespace-pre-wrap text-sm leading-relaxed">
              {enquiry.notes}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
        {label}
      </span>
      <span className="min-w-0 truncate text-right text-xs">{children}</span>
    </div>
  );
}

function StageTimeline({
  history,
  stages,
  currentStageKey,
}: {
  history: NonNullable<ReturnType<typeof useEnquiry>["data"]>["stage_history"];
  stages: AdmissionFunnelStage[];
  currentStageKey: string;
}) {
  // Reverse so newest sits at top — most useful by default. The list
  // also includes the initial stage entry (from_stage_key=null) which
  // we render with friendlier copy.
  const ordered = [...history].reverse();

  const stageMap = React.useMemo(() => {
    const m = new Map<string, AdmissionFunnelStage>();
    for (const s of stages) m.set(s.stage_key, s);
    return m;
  }, [stages]);

  // Same trick as ActivityTimeline — the BE persists raw user IDs in
  // the transition's `by` field, so we resolve to staff names client
  // side using the cached roster query.
  const { data: rosterData } = useStaffRoster();
  const resolveActor = React.useCallback(
    (userId: string | null | undefined): string | null => {
      if (!userId) return null;
      const s = rosterData?.items?.find((x) => x.user_id === userId);
      if (!s) return userId.slice(0, 8);
      return s.full_name?.trim() || s.email;
    },
    [rosterData?.items],
  );

  return (
    <Card>
      <CardHeader className="border-border/60 border-b pb-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <History className="text-muted-foreground size-4" aria-hidden="true" />
          Stage history
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-5">
        {ordered.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            No transitions recorded yet — this enquiry was just created.
          </p>
        ) : (
          <ol className="space-y-4">
            {ordered.map((t, idx) => {
              const toStage = stageMap.get(t.to_stage_key);
              const fromStage = t.from_stage_key ? stageMap.get(t.from_stage_key) : null;
              const isLatest = idx === 0;
              const isCurrent = isLatest && t.to_stage_key === currentStageKey;
              return (
                <li key={`${t.at}-${idx}`} className="flex gap-3">
                  <div className="relative flex flex-col items-center">
                    <span
                      className={cn(
                        "ring-background relative inline-flex size-3 rounded-full ring-4",
                        isCurrent
                          ? "bg-primary"
                          : toStage?.terminal_outcome === "admitted"
                            ? "bg-emerald-500"
                            : toStage?.terminal_outcome === "rejected"
                              ? "bg-rose-500"
                              : "bg-muted-foreground/40",
                      )}
                      aria-hidden="true"
                    />
                    {idx < ordered.length - 1 && (
                      <span className="bg-border/60 mt-1 w-px flex-1" aria-hidden="true" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 pb-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="text-foreground text-sm font-medium">
                        {fromStage ? (
                          <>
                            {fromStage.label}{" "}
                            <ArrowRight
                              className="text-muted-foreground inline size-3"
                              aria-hidden="true"
                            />{" "}
                            {toStage?.label ?? t.to_stage_key}
                          </>
                        ) : (
                          <>Created in {toStage?.label ?? t.to_stage_key}</>
                        )}
                      </span>
                      {isCurrent && (
                        <Badge variant="secondary" className="text-[10px]">
                          Current
                        </Badge>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-0.5 text-xs tabular-nums">
                      {formatDateTime(t.at)}
                      {t.by && (
                        <>
                          <span className="text-muted-foreground/60 mx-1.5">·</span>
                          by {resolveActor(t.by) ?? "system"}
                        </>
                      )}
                    </div>
                    {t.note && (
                      <p className="text-foreground/90 bg-muted/40 mt-2 rounded-md px-3 py-2 text-xs leading-relaxed">
                        {t.note}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="bg-muted h-3 w-24 animate-pulse rounded" />
          <div className="bg-muted h-9 w-full animate-pulse rounded-md" />
        </div>
      ))}
    </div>
  );
}

function NoFormEmptyState({ stageKey }: { stageKey: string }) {
  return (
    <div className="border-border/60 bg-muted/30 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-10 text-center">
      <div className="text-muted-foreground rounded-full p-2">
        <AlertCircle className="size-5" aria-hidden="true" />
      </div>
      <p className="text-foreground text-sm font-medium">No form linked to this stage</p>
      <p className="text-muted-foreground max-w-sm text-xs leading-relaxed">
        Stage <code className="bg-muted rounded px-1 py-0.5 font-mono">{stageKey}</code> doesn't
        have a form configured yet. Ask a Super Admin to link one from the Admissions tab in the SA
        portal.
      </p>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="bg-muted h-4 w-40 animate-pulse rounded" />
        <div className="bg-muted h-8 w-72 animate-pulse rounded" />
        <div className="bg-muted h-3 w-56 animate-pulse rounded" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="bg-card border-border/60 space-y-4 rounded-lg border p-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="bg-muted h-3 w-24 animate-pulse rounded" />
                <div className="bg-muted h-9 w-full animate-pulse rounded" />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-card border-border/60 h-48 animate-pulse rounded-lg border" />
          <div className="bg-card border-border/60 h-32 animate-pulse rounded-lg border" />
        </div>
      </div>
    </div>
  );
}

function NotFoundOrError({
  title,
  description,
  testId,
}: {
  title: string;
  description: string;
  testId?: string;
}) {
  return (
    <div
      className="border-border/60 bg-card mx-auto mt-12 max-w-md rounded-lg border p-8 text-center"
      data-testid={testId}
    >
      <div className="text-muted-foreground mx-auto mb-3 inline-flex size-10 items-center justify-center rounded-full bg-rose-500/10 text-rose-600">
        <AlertCircle className="size-5" aria-hidden="true" />
      </div>
      <h2 className="text-foreground text-base font-semibold">{title}</h2>
      <p className="text-muted-foreground mt-1 text-sm">{description}</p>
      <Button asChild variant="outline" size="sm" className="mt-4">
        <Link to="/admissions">
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back to admissions
        </Link>
      </Button>
    </div>
  );
}
